import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, ChevronUp, ChevronDown, Star, ExternalLink, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", display_name: "", website_url: "", logo_url: "", active: true };

export default function TrustedClients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/trusted-clients");
      setClients(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load");
    }
  };

  useEffect(() => { load(); }, []);

  const active = useMemo(() => clients.filter((c) => c.active), [clients]);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...empty, ...c, display_name: c.display_name || "", website_url: c.website_url || "", logo_url: c.logo_url || "" }); setOpen(true); };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        display_name: form.display_name?.trim() || null,
        website_url: form.website_url?.trim() || null,
        logo_url: form.logo_url?.trim() || null,
        active: !!form.active,
      };
      if (editing) {
        await api.patch(`/trusted-clients/${editing.client_id}`, payload);
        toast.success("Client updated");
      } else {
        await api.post("/trusted-clients", payload);
        toast.success("Client added");
      }
      setOpen(false); setEditing(null); setForm(empty);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/trusted-clients/${c.client_id}`);
      toast.success("Deleted");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/trusted-clients/${c.client_id}`, { active: !c.active });
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= clients.length) return;
    const reordered = [...clients];
    const [row] = reordered.splice(idx, 1);
    reordered.splice(target, 0, row);
    setClients(reordered); // optimistic
    try {
      await api.post("/trusted-clients/reorder", { order: reordered.map((r) => r.client_id) });
    } catch (err) {
      toast.error("Reorder failed");
      await load();
    }
  };

  return (
    <DashboardLayout
      title="Client Marquee"
      subtitle="Manage the public 'Trusted By' marquee shown on the homepage"
      right={
        <button data-testid="tclient-new" onClick={openNew}
          className="btn-scale h-9 px-3 rounded-md bg-blue-500 hover:bg-blue-400 text-white font-medium text-sm flex items-center gap-2 shadow-md shadow-blue-500/30">
          <Plus className="w-4 h-4" /> Add Client
        </button>
      }
    >
      <div className="space-y-6" data-testid="tclient-page">
        {/* Table */}
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 overflow-hidden">
          <div className="grid grid-cols-12 gap-3 px-4 py-3 text-[11px] uppercase tracking-widest text-slate-500 border-b border-slate-800">
            <div className="col-span-1 text-center">Order</div>
            <div className="col-span-4">Company</div>
            <div className="col-span-3">Website</div>
            <div className="col-span-2">Added</div>
            <div className="col-span-1 text-center">Active</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>
          {clients.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500" data-testid="tclient-empty">
              No clients yet. Add your first company to populate the marquee.
            </div>
          ) : clients.map((c, i) => (
            <div key={c.client_id}
                 data-testid={`tclient-row-${c.client_id}`}
                 className={`grid grid-cols-12 gap-3 px-4 py-3 items-center border-b border-slate-800/60 hover:bg-slate-800/30 ${!c.active ? "opacity-50" : ""}`}>
              <div className="col-span-1 flex items-center justify-center gap-1">
                <button data-testid={`tclient-up-${c.client_id}`} onClick={() => move(i, -1)} disabled={i === 0}
                        className="w-6 h-6 grid place-items-center rounded border border-slate-700 hover:border-blue-500/50 disabled:opacity-30">
                  <ChevronUp className="w-3 h-3" />
                </button>
                <button data-testid={`tclient-down-${c.client_id}`} onClick={() => move(i, 1)} disabled={i === clients.length - 1}
                        className="w-6 h-6 grid place-items-center rounded border border-slate-700 hover:border-blue-500/50 disabled:opacity-30">
                  <ChevronDown className="w-3 h-3" />
                </button>
              </div>
              <div className="col-span-4">
                <div className="text-sm font-semibold text-slate-100">{c.display_name || c.name}</div>
                {c.display_name && <div className="text-xs text-slate-500">{c.name}</div>}
              </div>
              <div className="col-span-3 text-sm">
                {c.website_url ? (
                  <a href={c.website_url} target="_blank" rel="noopener noreferrer"
                     data-testid={`tclient-web-${c.client_id}`}
                     className="text-blue-400 hover:text-blue-300 inline-flex items-center gap-1 truncate">
                    <ExternalLink className="w-3 h-3 shrink-0" />
                    <span className="truncate">{c.website_url.replace(/^https?:\/\//, "")}</span>
                  </a>
                ) : (
                  <span className="text-slate-600 text-xs">—</span>
                )}
              </div>
              <div className="col-span-2 text-xs text-slate-400">
                {c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}
              </div>
              <div className="col-span-1 text-center">
                <button data-testid={`tclient-toggle-${c.client_id}`} onClick={() => toggleActive(c)}
                        className={`w-9 h-5 rounded-full relative transition-colors ${c.active ? "bg-emerald-500/70" : "bg-slate-700"}`}
                        aria-label={c.active ? "Deactivate" : "Activate"}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${c.active ? "translate-x-4" : "translate-x-0.5"}`} />
                </button>
              </div>
              <div className="col-span-1 flex items-center justify-end gap-1">
                <button data-testid={`tclient-edit-${c.client_id}`} onClick={() => openEdit(c)}
                        className="w-7 h-7 grid place-items-center rounded border border-slate-700 hover:border-blue-500/50 text-slate-300 hover:text-blue-300">
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button data-testid={`tclient-delete-${c.client_id}`} onClick={() => remove(c)}
                        className="w-7 h-7 grid place-items-center rounded border border-slate-700 hover:border-rose-500/50 text-slate-300 hover:text-rose-300">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
          <div className="px-4 py-3 text-[11px] text-slate-500 border-t border-slate-800 flex items-center justify-between">
            <span>{clients.length} total · {active.length} active</span>
            <span className="flex items-center gap-1"><Star className="w-3 h-3 text-blue-400" /> Only <b className="text-slate-300">active</b> clients appear on the public marquee</span>
          </div>
        </div>

        {/* Live Preview */}
        <div className="rounded-xl border border-blue-500/20 overflow-hidden" data-testid="tclient-preview">
          <div className="px-4 py-3 border-b border-slate-800 bg-slate-900/60 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-blue-400" />
              <span className="text-sm font-semibold text-slate-100">Live preview — this is what visitors see</span>
            </div>
            <span className="text-[11px] uppercase tracking-widest text-slate-500">Auto-updates</span>
          </div>
          <MarqueePreview clients={active} />
        </div>
      </div>

      {/* Add/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border border-slate-800 text-slate-100" data-testid="tclient-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Client" : "Add Client"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-slate-500 block mb-1.5">Company Name *</span>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     data-testid="tclient-input-name"
                     className="w-full h-10 bg-slate-950 border border-slate-800 rounded-md px-3 text-sm focus:border-blue-500 outline-none" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-slate-500 block mb-1.5">Short display name <span className="text-slate-600">(optional)</span></span>
              <input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                     data-testid="tclient-input-display"
                     placeholder="Shown on the marquee instead of full name"
                     className="w-full h-10 bg-slate-950 border border-slate-800 rounded-md px-3 text-sm focus:border-blue-500 outline-none" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-slate-500 block mb-1.5">Website URL <span className="text-slate-600">(optional)</span></span>
              <input type="url" value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })}
                     data-testid="tclient-input-website"
                     placeholder="https://example.com"
                     className="w-full h-10 bg-slate-950 border border-slate-800 rounded-md px-3 text-sm focus:border-blue-500 outline-none" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-slate-500 block mb-1.5">Logo URL <span className="text-slate-600">(optional, for future use)</span></span>
              <input type="url" value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                     data-testid="tclient-input-logo"
                     placeholder="https://cdn.example.com/logo.png"
                     className="w-full h-10 bg-slate-950 border border-slate-800 rounded-md px-3 text-sm focus:border-blue-500 outline-none" />
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <button type="button"
                      onClick={() => setForm({ ...form, active: !form.active })}
                      data-testid="tclient-input-active"
                      className={`w-9 h-5 rounded-full relative transition-colors ${form.active ? "bg-emerald-500/70" : "bg-slate-700"}`}>
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${form.active ? "translate-x-4" : "translate-x-0.5"}`} />
              </button>
              <span className="text-sm text-slate-200">Active — visible on the public marquee</span>
            </label>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="btn-scale h-9 px-4 rounded-md border border-slate-700 text-slate-200 text-sm">Cancel</button>
            <button onClick={submit} disabled={loading} data-testid="tclient-submit"
                    className="btn-scale h-9 px-4 rounded-md bg-blue-500 hover:bg-blue-400 text-white text-sm font-semibold disabled:opacity-50">
              {loading ? "Saving…" : editing ? "Save changes" : "Add client"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function MarqueePreview({ clients }) {
  if (!clients?.length) {
    return (
      <div className="py-10 text-center text-sm text-slate-500" style={{ background: "#0A1530" }} data-testid="tclient-preview-empty">
        <EyeOff className="w-4 h-4 inline mr-2 opacity-60" /> No active clients — marquee will not render on the homepage.
      </div>
    );
  }
  const loop = [...clients, ...clients];
  return (
    <div className="relative py-8 overflow-hidden" style={{ background: "linear-gradient(180deg,#0A1530 0%,#0C1839 100%)" }}>
      <div className="text-center mb-6 px-4">
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: "#2B87F5" }}>
          Trusted By Local Businesses &amp; Organizations
        </div>
      </div>
      <div className="trust-marquee relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 w-16 z-10"
             style={{ background: "linear-gradient(90deg,#0A1530 0%,transparent 100%)" }} />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-16 z-10"
             style={{ background: "linear-gradient(270deg,#0A1530 0%,transparent 100%)" }} />
        <div className="trust-marquee-track flex items-center gap-10 whitespace-nowrap w-max">
          {loop.map((c, i) => (
            <div key={`${c.client_id}-${i}`} className="flex items-center gap-3 shrink-0">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#2B87F5", boxShadow: "0 0 10px rgba(43,135,245,0.7)" }} />
              <span className="font-display font-bold text-white text-base tracking-wide">
                {c.display_name || c.name}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
